import { errCode } from './apiError';

// The factory line runs on IST; prodqc records UTC with an offset. Every clock on the
// console formats through here so no page falls back to the browser's zone.
const IST = 'Asia/Kolkata';

const inIst = (options) => new Intl.DateTimeFormat('en-GB', { timeZone: IST, ...options });

const parse = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

// A calendar day from the snapshot ("2026-10-24") is an IST day, so it is pinned to IST
// midnight; parsing it bare would read it as UTC and shift it in a western browser.
const istDate = (ymd) => parse(ymd && ymd.length === 10 ? `${ymd}T00:00:00+05:30` : ymd);

// Chromium's ICU writes "Sat, 24 Oct" where Node writes "Sat 24 Oct", so the weekday is
// formatted on its own and joined by hand; every stamp then reads the same everywhere.
const weekday = (date, style) => inIst({ weekday: style }).format(date);
// ICU's en-GB short month is "Sept"; the line and prodqc write three letters.
const dayMonth = (date, month) =>
  inIst({ day: month === 'long' ? 'numeric' : '2-digit', month })
    .format(date)
    .replace('Sept', 'Sep');

/** "16:39" */
export const istTime = (iso) => {
  const date = parse(iso);
  return date ? inIst({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date) : '';
};

/** "Sat 24 Oct" */
export const istDay = (iso) => {
  const date = parse(iso);
  return date ? `${weekday(date, 'short')} ${dayMonth(date, 'short')}` : '';
};

/** "Sat 24 Oct · 16:39", the stamp every table and timeline uses. */
export const istStamp = (iso) => {
  const date = parse(iso);
  return date ? `${istDay(date)} · ${istTime(date)}` : '';
};

/** "Saturday 24 October" for a snapshot day. */
export const istLongDay = (ymd) => {
  const date = istDate(ymd);
  return date ? `${weekday(date, 'long')} ${dayMonth(date, 'long')}` : '';
};

/** "24 Oct" for a snapshot day. */
export const istShortDay = (ymd) => {
  const date = istDate(ymd);
  return date ? inIst({ day: 'numeric', month: 'short' }).format(date).replace('Sept', 'Sep') : '';
};

const dayNumber = (date) => inIst({ day: 'numeric' }).format(date);

/** "TUE 20", the per-day column header. */
export const istDayHeader = (ymd) => {
  const date = istDate(ymd);
  return date ? `${weekday(date, 'short')} ${dayNumber(date)}`.toUpperCase() : '';
};

/** "Tue" for the bench bars. */
export const istWeekday = (ymd) => {
  const date = istDate(ymd);
  return date ? weekday(date, 'short') : '';
};

/** "Tue 20 – Sat 24 October" for the week the Quality page covers. */
export const weekRangeLabel = (week = []) => {
  const first = istDate(week[0]);
  const last = istDate(week[week.length - 1]);
  if (!first || !last) return '';
  const short = (date) => `${weekday(date, 'short')} ${dayNumber(date)}`;
  return `${short(first)} – ${short(last)} ${inIst({ month: 'long' }).format(last)}`;
};

export const isSameIstDay = (iso, ymd) => {
  const date = parse(iso);
  if (!date || !ymd) return false;
  return (
    new Intl.DateTimeFormat('en-CA', {
      timeZone: IST,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date) === ymd
  );
};

/** "1,779" - en-GB groups by thousands whatever the browser's locale. */
export const n = (value) =>
  Number.isFinite(value)
    ? value.toLocaleString('en-GB')
    : value === null
      ? ''
      : String(value ?? '');

// Rounded, except that one failure in 294 reads 99%: 100% is reserved for no failures.
export const pct = (part, whole) => {
  if (!(whole > 0) || !Number.isFinite(part)) return '—';
  const value = Math.round((part / whole) * 100);
  return `${value === 100 && part < whole ? 99 : value}%`;
};

// The benches print a hyphen; the design draws a minus.
export const dbm = (rssi) => (Number.isFinite(rssi) ? `${String(rssi).replace('-', '−')} dBm` : '');

export const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

// A snapshot lands every minute while the line is filing; ten minutes of silence is
// the point at which the page stops calling itself live.
export const STALE_AFTER_MS = 10 * 60 * 1000;

// How old the snapshot is, by the server's clock: age_seconds when the line was read,
// plus the time since. The browser's own clock is never compared with received_at.
export const snapshotAgeMs = (data, fetchedAt, now = Date.now()) => {
  if (!data || !Number.isFinite(data.age_seconds) || !Number.isFinite(fetchedAt)) return null;
  return data.age_seconds * 1000 + Math.max(0, now - fetchedAt);
};

export const isStale = (ageMs) => ageMs === null || ageMs > STALE_AFTER_MS;

/** "42 s ago", "3 min ago", "2 h ago", "3 d ago" */
export const receivedAgo = (ageMs) => {
  if (ageMs === null || ageMs === undefined) return '';
  const seconds = Math.max(0, Math.floor(ageMs / 1000));
  if (seconds < 120) return `${seconds} s ago`;
  if (seconds < 7200) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 172800) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86400)} d ago`;
};

// Every production read answers through this shape so a view can route on the code.
export const productionError = (error, fallback) => ({
  status: error?.response?.status ?? null,
  code: errCode(error),
  message: error?.response?.data?.error?.message || error?.message || fallback,
});

export const isNoSnapshot = (error) => error?.code === 'snapshot_not_received';
// IsFirmwareReader refuses with DRF's code-less 403; an expired session never reaches a
// view, because the axios interceptor ends it first (lib/session.js).
export const isNotPermitted = (error) => error?.status === 403;
// The flag answers 404 with its own code; device_not_found is an ordinary miss.
export const isSwitchedOff = (error) => error?.code === 'feature_disabled';

/** The header capsule for a failed line read: a quiet state, or a fault worth red. */
export const lineErrorCapsule = (error) => {
  if (isNoSnapshot(error)) return { text: 'No snapshot yet', fault: false };
  if (isSwitchedOff(error)) return { text: 'Switched off', fault: false };
  if (isNotPermitted(error)) return { text: 'No access', fault: false };
  return { text: 'Not reachable', fault: true };
};

/** "merged_0.0.0+2_BURNIN_PD_LPS4012-155.hex" -> "0.0.0+2"; a bare version passes through. */
export const fwVersion = (name) => {
  if (!name) return '';
  const match = /\d+\.\d+\.\d+(\+\d+)?/.exec(name);
  return match ? match[0] : name;
};

export const STAGE_NAMES = {
  board: 'Board test',
  firmware: 'Final firmware',
  device_flash: 'Final firmware flashed',
  semi: 'Semi test',
  full: 'Full test',
  bind: 'Bind',
  charger_self: 'Charger self-test',
  charger_qc: 'Charger QC',
  packing: 'Packing',
  customer: 'Customer',
  finished: 'Finished · every stage passed',
};

export const stageName = (stage) => STAGE_NAMES[stage] || stage || '';

// The Line page's nine device boxes, in lane order, and the charger lane's six.
export const DEVICE_BOXES = [
  { key: 'wait_semi', label: 'Waiting for Semi test' },
  { key: 'wait_full', label: 'Waiting for Full test' },
  { key: 'ready_bind', label: 'Ready for Bind' },
  { key: 'bound_hold', label: 'On hold' },
  { key: 'finished', label: 'Finished' },
  { key: 'failed', label: 'Failed' },
  { key: 'no_board', label: 'No Board test' },
  { key: 'no_fw', label: 'No final firmware' },
  { key: 'full_no_semi', label: 'Out of order' },
];

export const CHARGER_BOXES = [
  { key: 'wait_qc', label: 'Waiting for Charger QC' },
  { key: 'ready_bind', label: 'Ready for Bind' },
  { key: 'bound_qc', label: 'In a set, Charger QC passed' },
  { key: 'bound_no_qc', label: 'On hold · bound without Charger QC' },
  { key: 'self_unfinished', label: 'Self-test not finished' },
  { key: 'failed', label: 'Failed' },
];

export const SET_STATUSES = [
  { key: 'finished', label: 'Finished' },
  { key: 'held', label: 'On hold' },
  { key: 'failed', label: 'Failed' },
];

// Grey is the normal state, amber a hold, red a failure, green only a finished result.
export const BOX_TONE = {
  bound_hold: 'warning',
  bound_no_qc: 'warning',
  failed: 'negative',
  finished: 'positive',
};

// What each bench checks, in the order the firmware prints them. The long form is the
// history page's sentence, the short form fits a table cell.
export const BOARD_CHECKS = {
  mcu: ['processor (MCU)', 'processor'],
  as: ['pulse sensor (AS7058)', 'pulse sensor'],
  accel: ['accelerometer', 'accelerometer'],
  flash: ['memory chip', 'memory chip'],
  ble: ['Bluetooth (BLE)', 'Bluetooth'],
};
export const SEMI_CHECKS = {
  antenna: ['antenna', 'antenna'],
  ppg: ['pulse sensor (PPG)', 'pulse sensor'],
  stim: ['stimulation', 'stimulation'],
  version: ['version', 'version'],
};
export const FULL_CHECKS = {
  ppg: ['pulse sensor', 'pulse sensor'],
  stim: ['stimulation', 'stimulation'],
  charging: ['charging', 'charging'],
};
export const CHARGER_SELF_CHECKS = {
  hall: ['lid sensor (hall)', 'lid sensor'],
  led: ['indicator lights', 'indicator lights'],
  boost: ['boost supply (power)', 'boost supply'],
  batt: ['battery', 'battery'],
  temp: ['temperature', 'temperature'],
  light: ['light sensor', 'light sensor'],
  mic: ['microphone', 'microphone'],
  ble: ['Bluetooth', 'Bluetooth'],
  ficr_match: ['chip ID', 'chip ID'],
};
export const CHARGER_QC_CHECKS = {
  hall: ['lid sensor', 'lid sensor'],
  usb: ['USB', 'USB'],
  haptic: ['vibration motor', 'vibration motor'],
};

/** Splits a bench's checks into the ones that passed, failed and were never reached. */
export const checkSummary = (checks = {}, labels, form = 0) => {
  const out = { passed: [], failed: [], blank: [], total: Object.keys(labels).length };
  Object.entries(labels).forEach(([key, names]) => {
    const value = checks?.[key];
    if (value === 'PASS') out.passed.push(names[form]);
    else if (value === 'FAIL') out.failed.push(names[form]);
    else out.blank.push(names[form]);
  });
  return out;
};

/** "lid sensor (hall), indicator lights, … — 9 of 9" or "battery, light sensor failed". */
export const checksSentence = (checks, labels) => {
  const { passed, failed, total } = checkSummary(checks, labels);
  if (failed.length) return `${failed.join(', ')} failed`;
  if (passed.length === total) return `${passed.join(', ')} — all passed`;
  if (passed.length) return `${passed.join(', ')} passed · ${total - passed.length} not reached`;
  return '';
};

// The benches as the line names them, keyed by the token each one files under. The
// tokens were named before the benches were renumbered, so four do not match.
export const BENCHES = [
  { token: 'station1', label: 'Station 1', role: 'Board test · final firmware', stage: 'board' },
  { token: 'station2', label: 'Station 2', role: 'Semi test', stage: 'semi' },
  { token: 'station6', label: 'Station 3', role: 'Full test', stage: 'full' },
  {
    token: 'station3',
    label: 'Station 4',
    role: 'Charger self-test · charger firmware',
    stage: 'charger_self',
  },
  { token: 'station4', label: 'Station 5', role: 'Charger QC', stage: 'charger_qc' },
  { token: 'station5', label: 'Station 6', role: 'Bind', stage: 'bind', testsLabel: 'bound today' },
  {
    token: 'DUSQHQ-1',
    label: 'Packing PC',
    role: 'Packing · shipping list',
    stage: 'packing',
    note: 'Its list reaches the dashboard by hand today. Once the packing tool files with this token, the Packing and Customer columns fill on their own.',
  },
];

export const benchFor = (token) => BENCHES.find((bench) => bench.token === token) || null;

// The token's own number, when it has one, so a renamed bench can say so.
export const tokenMismatch = (bench) =>
  Boolean(bench) &&
  !/^(DUSQHQ|backend)/.test(bench.token) &&
  bench.token !== `station${bench.label.replace(/\D/g, '')}`;

/** "Board test bench (Station 1)", or the raw token for a PC the server does not know. */
export const benchName = (token) => {
  const bench = benchFor(token);
  if (!bench) return token ? `an unknown PC (${token})` : 'an unknown bench';
  return `${stageName(bench.stage)} bench (${bench.label})`;
};

/** "Bind bench" */
export const benchShortName = (token) => {
  const bench = benchFor(token);
  return bench ? `${stageName(bench.stage)} bench` : token || '';
};

// Where the header search sends a term, from its shape: serials and charger numbers
// have their own lists; everything else is a device number or a chip ID.
export const searchTarget = (term) => {
  const value = term.trim().toUpperCase();
  if (/^DUSQOEL/.test(value)) return 'sets';
  if (/^CHG/.test(value)) return 'chargers';
  return 'devices';
};

const flatten = (row, prefix = '') =>
  Object.entries(row || {}).reduce((out, [key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return { ...out, ...flatten(value, `${prefix}${key}.`) };
    }
    return { ...out, [`${prefix}${key}`]: value };
  }, {});

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = Array.isArray(value) ? value.join('; ') : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Rows to CSV; nested check objects flatten to "s1_checks.mcu" columns. */
export const toCsv = (rows) => {
  const flat = rows.map((row) => flatten(row));
  const columns = [...new Set(flat.flatMap((row) => Object.keys(row)))];
  return [columns, ...flat.map((row) => columns.map((column) => row[column]))]
    .map((line) => line.map(csvCell).join(','))
    .join('\n');
};

// The lists are bounded (~1,800 rows today) and the server pages at 200, so a
// download walks the pages of the current filter rather than asking for a new endpoint.
export const collectAll = async (fetchPage, params, pageSize = 200, maxPages = 100) => {
  const rows = [];
  for (let page = 1; page <= maxPages; page += 1) {
    // Pages are sequential by design: each one decides whether another is needed.
    const response = await fetchPage({ ...params, page, limit: pageSize });
    rows.push(...(response?.data || []));
    if (rows.length >= (response?.total_records || 0) || !(response?.data || []).length) break;
  }
  return rows;
};

export const saveTextFile = (name, text, type = 'text/csv') => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
